/* eslint-disable jsx-a11y/anchor-is-valid */
/* eslint-disable eqeqeq */
import React, { useEffect, useMemo, useState } from 'react';
import { Button, Dropdown, Table, Modal } from 'react-bootstrap';
import ReactPaginate from 'react-paginate';
import { PropagateLoader } from 'react-spinners';
import { useTranslate } from '../../hooks/LanguageProvider';
import { toTimestamp } from '../../utilities/functions';
import ExcelJS from 'exceljs';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { toast, Bounce } from 'react-toastify';
import { NavLink } from 'react-router-dom';

const backendUrl = process.env.REACT_APP_BACKEND_URL;

interface ReportData {
  id_repport: number;
  driver_name: string;
  ibutton_code: string;
  psn: string;
  distance: number;
  acc: number;
  brk: number;
  max_speed: number;     // utilisé ici comme PÉNALITÉ "speeding"
  duration: string;
  start: string;
  end: string;
  score: number;
  rest_time: string;
  driving_time: string;
  hos_penalties: number;
}

interface RecapData {
  id_repport: number;
  type_report: number;
  turn_report: number;
  date_creation: string;
  date_debut: string;
  date_fin: string;
}

interface Report35Props {
  type_report?: string;
  turn?: string;
  id_report?: string;
}

const Report35: React.FC<Report35Props> = ({ turn }) => {
  const { translate } = useTranslate();

  // ─────────────── States data ───────────────
  const [reports, setReports] = useState<ReportData[]>([]);
  const [recap, setRecap] = useState<RecapData | undefined>();
  const [loading, setLoading] = useState<boolean>(true);

  // pagination
  const [limit, setLimit] = useState<number>(10);
  const [currentPage, setCurrentPage] = useState<number>(0); // 0-based UI
  const [totalRows, setTotalRows] = useState<number>(0);

  // tri + recherche
  const [colum, setColum] = useState<string>('driver_name');
  const [sort, setSort] = useState<'ASC' | 'DESC'>('ASC');
  const [search, setSearch] = useState<string>('');
  const [type, setType] = useState<number>(0);
  const [typeSearch, setTypeSearch] = useState<string>(translate('Report ID'));

  // export modal
  const [showDownload, setShowDownload] = useState<boolean>(false);

  const pageCount = useMemo(() => {
    return Math.max(1, Math.ceil(totalRows / Math.max(1, limit)));
  }, [totalRows, limit]);

  // ─────────────── Colonnes visibles ───────────────
  const [selectedColumns, setSelectedColumns] = useState({
    id_repport: true,
    ibutton_code: true,
    driver_name: true,
    psn: true,
    distance: true,
    acc: true,
    brk: true,
    speeding_penalty: true,  // affichera data.max_speed
    hos_penalties: true,
    driving_time: true,
    rest_time: true,
    score: true,
  });

  const labelsMap: Record<keyof typeof selectedColumns, string> = {
    id_repport: translate('Report ID'),
    ibutton_code: translate('iButton Code'),
    driver_name: translate('Driver Name'),
    psn: translate('PSN'),
    distance: translate('Distance (Km)'),
    acc: translate('Acceleration'),
    brk: translate('Braking'),
    speeding_penalty: `${translate('Speeding violation')} – Penalty`,
    hos_penalties: translate('HOS Penalty'),
    driving_time: translate('Driving Time'),
    rest_time: translate('Rest Time'),
    score: translate('Total Score'),
  };

  const handleColumnChange = (key: keyof typeof selectedColumns) => {
    setSelectedColumns(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const getScoreClass = (score: number) => {
    if (score < 2) return 'table-success';
    if (score < 5) return 'table-warning';
    return 'table-danger';
  };

  // ─────────────── Fetch backend ───────────────
  async function getReport(params: {
    page0: number; // 0-based
    limit: number;
    search: string;
    type: number;
    colum: string;
    sort: 'ASC' | 'DESC';
  }) {
    const { page0, limit, search, type, colum, sort } = params;
    const page1 = page0 + 1;

    setLoading(true);
    try {
      const bodyData = JSON.stringify({ limit, page: page1, search, type, turn, colum, sort });

      const totalRes = await fetch(`${backendUrl}/api/report35/totalpage`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: bodyData,
        mode: 'cors',
      });
      const totalJson = await totalRes.json();
      setTotalRows(Number(totalJson?.[0]?.total || 0));

      const dataRes = await fetch(`${backendUrl}/api/report35`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: bodyData,
        mode: 'cors',
      });
      const dataJson = await dataRes.json();
      setReports(Array.isArray(dataJson?.reportData) ? dataJson.reportData : []);
      setRecap(dataJson?.recap);
    } catch (e) {
      console.error('getReport error:', e);
      setReports([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    getReport({ page0: currentPage, limit, search, type, colum, sort });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, limit, colum, sort, type, turn]);

  useEffect(() => {
    setCurrentPage(0);
  }, [search]);

  const handlePageClick = (ev: { selected: number }) => setCurrentPage(ev.selected);
  const handleSelectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setLimit(Number(e.target.value) || 10);
    setCurrentPage(0);
  };
  const handleSortingColum = (col: string) => {
    setColum(col);
    setSort(prev => (prev === 'ASC' ? 'DESC' : 'ASC'));
    setCurrentPage(0);
  };
  const handleAdvancedSearch = (e: React.ChangeEvent<HTMLInputElement>) => setSearch(e.target.value || '');
  const handleResetSearch = () => setSearch('');

  const menuItems = [
    translate('Report ID'),
    translate('Driver name'),
    translate('iButton code'),
    translate('PSN'),
    translate('Distance'),
    translate('ACC'),
    translate('BRK'),
    translate('Start time'),
    translate('End time'),
    translate('Score'),
    translate('Rest time'),
    translate('Driving time'),
  ];
  const handleTypeSearch = (selected: string) => {
    switch (selected) {
      case translate('Report ID'): setType(0); break;
      case translate('Driver name'): setType(1); break;
      case translate('iButton code'): setType(2); break;
      case translate('PSN'): setType(3); break;
      case translate('Distance'): setType(4); break;
      case translate('ACC'): setType(5); break;
      case translate('BRK'): setType(6); break;
      case translate('Start time'): setType(9); break;
      case translate('End time'): setType(10); break;
      case translate('Score'): setType(11); break;
      case translate('Rest time'): setType(12); break;
      case translate('Driving time'): setType(13); break;
      default: break;
    }
    setTypeSearch(selected);
    setCurrentPage(0);
  };

  function copyToClipboard(tagValue: any) {
    const t = document.createElement('textarea');
    t.value = String(tagValue ?? '');
    document.body.appendChild(t);
    t.select();
    document.execCommand('copy');
    document.body.removeChild(t);
  }

  const user_id = localStorage.getItem('userid');
  useEffect(() => setCurrentPage(0), [user_id]);

  // ─────────────── Helpers EXPORT ───────────────
  function buildHeadersAndRows() {
    const headers: string[] = [];
    const pushIf = (key: keyof typeof selectedColumns, label: string) => {
      if (selectedColumns[key]) headers.push(label);
    };

    pushIf('id_repport', labelsMap.id_repport);
    pushIf('ibutton_code', translate('Tag'));
    pushIf('driver_name', translate('Drivers'));
    pushIf('psn', translate('Vehicles'));
    pushIf('distance', `${translate('Distance')} (Km)`);
    pushIf('acc', labelsMap.acc);
    pushIf('brk', labelsMap.brk);
    pushIf('speeding_penalty', translate('Speeding violation')); // 1 seule col
    pushIf('hos_penalties', translate('HOS Penalty'));
    pushIf('driving_time', translate('Driving'));
    pushIf('rest_time', translate('Rest'));
    pushIf('score', translate('Total score'));

    const rows = reports.map((r) => {
      const row: any[] = [];
      const add = (cond: boolean, val: any) => { if (cond) row.push(val); };

      add(selectedColumns.id_repport, r.id_repport);
      add(selectedColumns.ibutton_code, r.ibutton_code ?? '');
      add(selectedColumns.driver_name, r.driver_name ?? '');
      add(selectedColumns.psn, r.psn ?? '');
      add(selectedColumns.distance, r.distance ?? 0);
      add(selectedColumns.acc, r.acc ?? 0);
      add(selectedColumns.brk, r.brk ?? 0);
      add(selectedColumns.speeding_penalty, r.max_speed ?? 0);
      add(selectedColumns.hos_penalties, r.hos_penalties ?? 0);
      add(selectedColumns.driving_time, r.driving_time ?? '');
      add(selectedColumns.rest_time, r.rest_time ?? '');
      add(selectedColumns.score, Number.isFinite(Number(r.score)) ? Number(r.score).toFixed(2) : r.score);

      return row;
    });

    return { headers, rows };
  }

  // 2) Export Excel (ExcelJS) avec styles propres
  async function exportExcelStyled() {
    try {
      const { headers, rows } = buildHeadersAndRows();

      const wb = new ExcelJS.Workbook();
      const ws = wb.addWorksheet('Driver behavior', {
        properties: { defaultRowHeight: 22 },
        views: [{ state: 'frozen', ySplit: 3 }], // titre + période + header
      });

      // Titre + période
      const title = `${translate('Driver behavior')} – ${translate('Turn')} ${recap?.turn_report ?? '-'}`;
      ws.mergeCells(1, 1, 1, Math.max(1, headers.length));
      ws.getCell(1, 1).value = title;
      ws.getCell(1, 1).font = { size: 14, bold: true };
      ws.getCell(1, 1).alignment = { vertical: 'middle', horizontal: 'center' };
      ws.getRow(1).height = 26;

      const period = `${translate('From')} ${toTimestamp(recap?.date_debut ?? '')} ${translate('to')} ${toTimestamp(recap?.date_fin ?? '')}`;
      ws.mergeCells(2, 1, 2, Math.max(1, headers.length));
      ws.getCell(2, 1).value = period;
      ws.getCell(2, 1).font = { size: 11, italic: true };
      ws.getCell(2, 1).alignment = { vertical: 'middle', horizontal: 'center' };
      ws.getRow(2).height = 20;

      // En-têtes (ligne 3)
      const headerRow = ws.addRow(headers);
      headerRow.eachCell((cell) => {
        cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF1F4E78' } };
        cell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
        cell.border = {
          top: { style: 'thin', color: { argb: 'FFAAAAAA' } },
          left: { style: 'thin', color: { argb: 'FFAAAAAA' } },
          bottom: { style: 'thin', color: { argb: 'FFAAAAAA' } },
          right: { style: 'thin', color: { argb: 'FFAAAAAA' } },
        };
      });
      ws.getRow(3).height = 24;

      // Lignes data
      rows.forEach((values, idx) => {
        const r = ws.addRow(values);
        r.eachCell((cell) => {
          const isEven = idx % 2 === 1;
          cell.fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: isEven ? 'FFF6F8FA' : 'FFFFFFFF' },
          };
          cell.border = {
            top: { style: 'hair', color: { argb: 'FFE0E0E0' } },
            left: { style: 'hair', color: { argb: 'FFE0E0E0' } },
            bottom: { style: 'hair', color: { argb: 'FFE0E0E0' } },
            right: { style: 'hair', color: { argb: 'FFE0E0E0' } },
          };
          const v = cell.value as any;
          if (typeof v === 'number' || (!isNaN(Number(v)) && v !== '')) {
            cell.alignment = { vertical: 'middle', horizontal: 'center' };
          } else {
            cell.alignment = { vertical: 'middle', horizontal: 'left' };
          }
        });
      });

      // Coloration conditionnelle Score
      if (selectedColumns.score) {
        const scoreColIndex = headers.length; // dernière colonne
        for (let r = 4; r <= ws.rowCount; r++) {
          const cell = ws.getRow(r).getCell(scoreColIndex);
          const num = Number(cell.value);
          if (!isNaN(num)) {
            if (num < 2) cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFD9F2D9' } };
            else if (num < 5) cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFF2CC' } };
            else cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF8D7DA' } };
          }
          cell.alignment = { vertical: 'middle', horizontal: 'center' };
          cell.font = { bold: true };
        }
      }

      // Auto-width SAFE (sans eachCell, donc pas de TS2722)
      const colCount = ws.columnCount; // fiable même avec merges
      for (let c = 1; c <= colCount; c++) {
        let max = 12;
        for (let r = 1; r <= ws.rowCount; r++) {
          const cell = ws.getRow(r).getCell(c);
          const v = cell?.value as any;
          const text =
            v == null
              ? ''
              : (typeof v === 'object' && 'richText' in v)
                ? (v.richText as any[]).map((rt: any) => rt?.text ?? '').join('')
                : String(v);
          max = Math.max(max, text.length + 2);
        }
        ws.getColumn(c).width = Math.min(40, max);
      }

      // Téléchargement
      const buf = await wb.xlsx.writeBuffer();
      const blob = new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const fname = `DriverBehavior_T${recap?.turn_report ?? '-'}_${(recap?.date_debut || '').slice(0, 10)}.xlsx`;
      a.download = fname;
      a.click();
      window.URL.revokeObjectURL(url);

      toast.success(translate('Excel file generated successfully'), {
        position: 'bottom-right', autoClose: 3000, theme: 'light', transition: Bounce,
      });
    } catch (err) {
      console.error('Excel export error:', err);
      toast.error(translate('An error occurred while generating Excel'), {
        position: 'bottom-right', autoClose: 3000, theme: 'light', transition: Bounce,
      });
    }
  }

  // 3) Export PDF (jsPDF + autoTable)
  function exportPDF() {
    try {
      const { headers, rows } = buildHeadersAndRows();
      const doc = new jsPDF('l', 'pt');
      doc.setFontSize(16);
      doc.text(`${translate('Driver behavior')} – ${translate('Turn')} ${recap?.turn_report ?? '-'}`, 40, 40);
      doc.setFontSize(10);
      doc.text(`${translate('From')} ${toTimestamp(recap?.date_debut ?? '')} ${translate('to')} ${toTimestamp(recap?.date_fin ?? '')}`, 40, 58);

      autoTable(doc, {
        head: [headers],
        body: rows,
        startY: 80,
        styles: { fontSize: 9, cellPadding: 4, halign: 'center' },
        headStyles: { fillColor: [31, 78, 120], textColor: 255, halign: 'center' },
        alternateRowStyles: { fillColor: [246, 248, 250] },
        margin: { left: 30, right: 30 },
      });

      doc.save(`DriverBehavior_T${recap?.turn_report ?? '-'}.pdf`);
      toast.success(translate('PDF generated successfully'), {
        position: 'bottom-right', autoClose: 3000, theme: 'light', transition: Bounce,
      });
    } catch (err) {
      console.error('PDF export error:', err);
      toast.error(translate('An error occurred while generating PDF'), {
        position: 'bottom-right', autoClose: 3000, theme: 'light', transition: Bounce,
      });
    }
  }

  return (
    <>
      <div className="row">
        <div className="col-sm-6 col-md-6">
          <h6>
            <i className="las la-chart-bar" /> {translate('Driver behavior')}{' '}
            Nr.Tournée: {recap?.turn_report}, {translate('From')}{' '}
            <span style={{ color: '#3468C0' }}>{toTimestamp(recap?.date_debut ?? '')}</span>{' '}
            {translate('to')}{' '}
            <span style={{ color: '#3468C0' }}>{toTimestamp(recap?.date_fin ?? '')}</span>
          </h6>
        </div>
        <div className="col-sm-6 col-md-6">
          <div className="float-right dataTables_filter mr-3">
            <NavLink to="/driver-behavior-config" className="btn btn-primary  mr-1">
              <i className="las la-cog mr-3"></i>
              {translate('Configuration')}
            </NavLink >
            <Button variant="outline-secondary" className="mr-2" onClick={() => setShowDownload(true)}>
              <i className="las la-download mr-2" />
              {translate('Download')}
            </Button>
          </div>
        </div>
      </div>

      {/* Barre recherche + gestion colonnes */}
      <div className="row m-2">
        <div className="col-sm-6 col-md-6" style={{ margin: '0 0 10px 0', padding: 0 }}>
          <div className="input-group">
            <Dropdown>
              <Dropdown.Toggle variant="link" id="dropdown-basic">
                <i className="fas fa-chevron-down" style={{ fontSize: 20 }} />
              </Dropdown.Toggle>
              <Dropdown.Menu>
                {menuItems.map((item, index) => (
                  <Dropdown.Item
                    key={index}
                    onClick={() => handleTypeSearch(item)}
                    eventKey={item}
                    active={typeSearch === item}
                    className={typeSearch === item ? 'select-active' : ''}
                  >
                    {item}
                  </Dropdown.Item>
                ))}
              </Dropdown.Menu>
            </Dropdown>
            <input
              type="text"
              placeholder={` ${translate('Search by')} ${translate(typeSearch)}`}
              onChange={handleAdvancedSearch}
              className="form-control"
              value={search}
            />
            <Button variant="secondary" onClick={handleResetSearch} className="btn-reset">
              <i className="las la-times" style={{ color: '#fff' }} />
            </Button>
          </div>
        </div>

        <div className="col-md-6 d-flex justify-content-end align-items-center">
          <div className="dataTables_length" id="DataTables_Table_0_length">
            <label style={{ float: 'right' }}>
              {translate('Show')}
              <select
                name="DataTables_Table_0_length"
                aria-controls="DataTables_Table_0"
                className="custom-select custom-select-sm form-control form-control-sm mr-2 ml-2"
                style={{ width: 66 }}
                onChange={handleSelectChange}
                value={String(limit)}
              >
                <option value="10">10</option>
                <option value="20">20</option>
                <option value="50">50</option>
                <option value="100">100</option>
                <option value="200">200</option>
                <option value="500">500</option>
              </select>
            </label>
          </div>

          <Dropdown>
            <Dropdown.Toggle variant="" id="dropdown-cols" title={translate('Display columns')}>
              <i className="las la-eye" />
            </Dropdown.Toggle>
            <Dropdown.Menu>
              {Object.keys(selectedColumns).map((key) => (
                <Dropdown.Item key={key} as="button" style={{ display: 'flex', alignItems: 'center' }}>
                  <input
                    type="checkbox"
                    className="form-check-input"
                    checked={(selectedColumns as any)[key]}
                    onChange={() => handleColumnChange(key as keyof typeof selectedColumns)}
                  />
                  <span style={{ marginLeft: 10 }}>
                    {labelsMap[key as keyof typeof selectedColumns]}
                  </span>
                </Dropdown.Item>
              ))}
            </Dropdown.Menu>
          </Dropdown>
        </div>
      </div>

      {/* Tableau */}
      <div className="row m-1 table-responsive">
        <Table className="dataTable">
          <thead className="bg-white">
            <tr>
              {selectedColumns.id_repport && (
                <th className="sorting" style={{ textAlign: 'left' }} onClick={() => handleSortingColum('id_repport')}>
                  {translate('Report ID')}
                </th>
              )}
              {selectedColumns.ibutton_code && (
                <th className="sorting" style={{ textAlign: 'left' }} onClick={() => handleSortingColum('ibutton_code')}>
                  {translate('Tag')}
                </th>
              )}
              {selectedColumns.driver_name && (
                <th className="sorting" style={{ textAlign: 'left' }} onClick={() => handleSortingColum('driver_name')}>
                  {translate('Drivers')}
                </th>
              )}
              {selectedColumns.psn && (
                <th className="sorting" style={{ textAlign: 'left' }} onClick={() => handleSortingColum('psn')}>
                  {translate('Vehicles')}
                </th>
              )}
              {selectedColumns.distance && (
                <th className="sorting" style={{ textAlign: 'left' }} onClick={() => handleSortingColum('distance')}>
                  {translate('Distance')} (Km)
                </th>
              )}
              {selectedColumns.acc && (
                <th className="sorting" onClick={() => handleSortingColum('acc')}>
                  {translate('Acceleration')}
                </th>
              )}
              {selectedColumns.brk && (
                <th className="sorting" onClick={() => handleSortingColum('brk')}>
                  {translate('Braking')}
                </th>
              )}
              {selectedColumns.speeding_penalty && (
                <th className="sorting" style={{ textAlign: 'center' }} onClick={() => handleSortingColum('max_speed')}>
                  {translate('Speeding violation')}
                </th>
              )}
              {selectedColumns.hos_penalties && (
                <th className="sorting" style={{ textAlign: 'center' }} onClick={() => handleSortingColum('hos_penalties')}>
                  {translate('HOS Penalty')}
                </th>
              )}
              {selectedColumns.driving_time && (
                <th className="sorting" style={{ textAlign: 'center' }} onClick={() => handleSortingColum('driving_time')}>
                  {translate('Driving')}
                </th>
              )}
              {selectedColumns.rest_time && (
                <th className="sorting" style={{ textAlign: 'center' }} onClick={() => handleSortingColum('rest_time')}>
                  {translate('Rest')}
                </th>
              )}
              {selectedColumns.score && (
                <th className="sorting" style={{ textAlign: 'left' }} onClick={() => handleSortingColum('score')}>
                  {translate('Total score')}
                </th>
              )}
            </tr>
          </thead>

          <tbody>
            {loading ? (
              <tr style={{ textAlign: 'center' }}>
                <td className="text-center" colSpan={12}>
                  <p>
                    <PropagateLoader loading={loading} size={20} />
                  </p>
                </td>
              </tr>
            ) : reports.length > 0 ? (
              reports.map((data) => (
                <tr key={data.id_repport} role="row">
                  {selectedColumns.id_repport && <td>{data.id_repport}</td>}
                  {selectedColumns.ibutton_code && (
                    <td
                      id={`tag-${data.ibutton_code}`}
                      onClick={() => copyToClipboard(data.ibutton_code)}
                      title="Cliquer pour copier"
                      style={{ cursor: 'pointer' }}
                    >
                      {data.ibutton_code && (
                        <>
                          <i className="las la-tags" style={{ marginRight: 4 }} />
                          <span style={{ color: '#007bff' }}>{data.ibutton_code}</span>
                        </>
                      )}
                    </td>
                  )}
                  {selectedColumns.driver_name && <td>{data.driver_name}</td>}
                  {selectedColumns.psn && <td>{data.psn}</td>}
                  {selectedColumns.distance && <td>{data.distance}</td>}
                  {selectedColumns.acc && <td>{data.acc}</td>}
                  {selectedColumns.brk && <td>{data.brk}</td>}
                  {selectedColumns.speeding_penalty && <td>{data.max_speed}</td>}
                  {selectedColumns.hos_penalties && <td>{data.hos_penalties}</td>}
                  {selectedColumns.driving_time && <td>{data.driving_time}</td>}
                  {selectedColumns.rest_time && <td>{data.rest_time}</td>}
                  {selectedColumns.score && (
                    <td className={getScoreClass(Number(data.score))}>
                      {Number.isFinite(Number(data.score)) ? Number(data.score).toFixed(2) : String(data.score)}
                    </td>
                  )}
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={12}>No report available</td>
              </tr>
            )}
          </tbody>
        </Table>
      </div>

      {/* Footer + pagination */}
      <div className="row">
        <div className="col-md-6 d-flex align-items-center">
          <span>
            {translate('Displaying')} {reports.length} {translate('on')} {totalRows}
          </span>
        </div>
        <div className="col-md-6 d-flex justify-content-end">
          <ReactPaginate
            previousLabel={translate('previous')}
            nextLabel={translate('next')}
            breakLabel={'...'}
            pageCount={pageCount}
            marginPagesDisplayed={2}
            pageRangeDisplayed={3}
            onPageChange={handlePageClick}
            containerClassName={'pagination justify-right'}
            pageClassName={'page-item'}
            pageLinkClassName={'page-link'}
            previousClassName={'page-item'}
            previousLinkClassName={'page-link'}
            nextClassName={'page-item'}
            nextLinkClassName={'page-link'}
            breakClassName={'page-item'}
            breakLinkClassName={'page-link'}
            activeClassName={'active'}
            forcePage={Math.min(currentPage, pageCount - 1)}
          />
        </div>
      </div>

      {/* Modal de choix de format */}
      <Modal show={showDownload} onHide={() => setShowDownload(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>{translate('Select Download Format')}</Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center">
          {translate('Please select the format to download the data')}
          <div className="mt-3 d-flex justify-content-center">
            <Button
              variant="success"
              className="mr-2"
              onClick={() => { setShowDownload(false); exportExcelStyled(); }}
            >
              Excel
            </Button>
            <Button
              variant="danger"
              onClick={() => { setShowDownload(false); exportPDF(); }}
            >
              PDF
            </Button>
          </div>
        </Modal.Body>
      </Modal>
    </>
  );
};

export default Report35;
